import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  User,
  Tag,
  Sliders,
  Plus,
  X,
  AlertCircle,
  Edit3,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';
import CustomRulesModal from './CustomRulesModal';
import { authFetch, getAccessToken } from '../utils/authFetch';

const PROFILE_SECTIONS = [
  { id: 'profile', label: 'Profile', icon: User },
  { id: 'alerts', label: 'Price Alerts', icon: AlertCircle },
  { id: 'keywords', label: 'Message Keywords', icon: Tag },
  { id: 'rules', label: 'Custom Rules', icon: Sliders },
];

function Profile() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [activeSection, setActiveSection] = useState('profile');
  const [portfolioMap, setPortfolioMap] = useState({});
  const [watchlistMap, setWatchlistMap] = useState({});
  const [keywords, setKeywords] = useState({ content: [], headline: [], sentiment: [], symbol: [] });
  const [settings, setSettings] = useState({ alert_preference: true });
  const [priceAlert, setPriceAlert] = useState({ portfolio: {}, watchlist: {} });
  const [customRules, setCustomRules] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);

  // Keywords form
  const [newKeyword, setNewKeyword] = useState('');
  const [keywordType, setKeywordType] = useState('headline');

  // Price alerts: which tab and which symbols are in edit mode
  const [alertTab, setAlertTab] = useState('portfolio');
  const [editingSymbols, setEditingSymbols] = useState({ portfolio: {}, watchlist: {} });

  const [rulesModalOpen, setRulesModalOpen] = useState(false);
  // Message Keywords: which category is expanded to show all keywords (default: show one row only)
  const [keywordCategoryExpanded, setKeywordCategoryExpanded] = useState({ headline: false, content: false, sentiment: false });
  const [personalInfoFormMode, setPersonalInfoFormMode] = useState('password'); // 'password' | 'phone'
  const [phoneStep, setPhoneStep] = useState('enter_phone');
  const [newPhoneValue, setNewPhoneValue] = useState('');
  const [phoneCode, setPhoneCode] = useState('');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [phoneError, setPhoneError] = useState('');
  const [passwordError, setPasswordError] = useState('');

  const userEmail = user?.email;

  const loadProfile = useCallback(async () => {
    if (!userEmail) return;
    setLoading(true);
    try {
      const [portfolioRes, watchlistRes, keywordsRes, settingsRes, priceAlertRes, rulesRes] = await Promise.all([
        authFetch(`/api/portfolio/${userEmail}`),
        authFetch(`/api/watchlist/${userEmail}`),
        authFetch(`/api/keywords/${userEmail}`),
        authFetch(`/api/settings/${userEmail}`),
        authFetch(`/api/price-alert/${userEmail}`),
        authFetch(`/api/rules/${userEmail}`),
      ]);

      const portfolioData = portfolioRes.ok ? await portfolioRes.json() : {};
      const watchlistData = watchlistRes.ok ? await watchlistRes.json() : {};
      const keywordsData = keywordsRes.ok ? await keywordsRes.json() : {};
      const settingsData = settingsRes.ok ? await settingsRes.json() : {};
      const priceAlertData = priceAlertRes.ok ? await priceAlertRes.json() : {};
      const rulesData = rulesRes.ok ? await rulesRes.json() : {};

      setPortfolioMap(portfolioData.portfolioMap || {});
      setWatchlistMap(watchlistData.watchlistMap || {});
      setKeywords(keywordsData.keywords || { content: [], headline: [], sentiment: [], symbol: [] });
      setSettings({
          alert_preference: settingsData.alert_preference === false ? false : true,
        });
      setPriceAlert(priceAlertData.price_alert || { portfolio: {}, watchlist: {} });
      setCustomRules(rulesData.rules || { buyRules: [], sellRules: [] });
    } catch (err) {
      console.error('Error loading profile:', err);
      setMessage({ type: 'error', text: 'Failed to load profile.' });
    } finally {
      setLoading(false);
    }
  }, [userEmail]);

  useEffect(() => {
    const stored = localStorage.getItem('user');
    const u = stored ? JSON.parse(stored) : null;
    setUser(u);
    if (!u) {
      navigate('/login');
      return;
    }
  }, [navigate]);

  useEffect(() => {
    if (userEmail) loadProfile();
  }, [userEmail, loadProfile]);

  const showMessage = (type, text) => {
    setMessage({ type, text });
    setTimeout(() => setMessage(null), 3000);
  };

  const handleSaveSettings = async () => {
    if (!userEmail) return;
    setSaving(true);
    try {
      const res = await authFetch(`/api/settings/${userEmail}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ alert_preference: settings.alert_preference }),
      });
      if (!res.ok) throw new Error('Failed to save settings');
      showMessage('success', 'Alert preference saved.');
    } catch (err) {
      showMessage('error', err.message || 'Failed to save settings.');
    } finally {
      setSaving(false);
    }
  };

  const handleAddKeyword = async (e) => {
    e.preventDefault();
    if (!newKeyword.trim() || !userEmail) return;
    setSaving(true);
    try {
      const val = keywordType === 'symbol' ? newKeyword.trim().toUpperCase() : newKeyword.trim();
      const res = await authFetch(`/api/keywords/${userEmail}/add-multiple?${keywordType}=${encodeURIComponent(val)}`);
      if (!res.ok) throw new Error('Failed to add keyword');
      const data = await res.json();
      setKeywords(data.keywords || keywords);
      setNewKeyword('');
      showMessage('success', 'Keyword added.');
    } catch (err) {
      showMessage('error', err.message || 'Failed to add keyword.');
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveKeyword = async (type, keyword) => {
    if (!userEmail) return;
    setSaving(true);
    try {
      const res = await authFetch(`/api/keywords/${userEmail}/remove-multiple`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ [type]: keyword }),
      });
      if (!res.ok) throw new Error('Failed to remove keyword');
      const data = await res.json();
      setKeywords(data.keywords || data.keyword || keywords);
      showMessage('success', 'Keyword removed.');
    } catch (err) {
      showMessage('error', err.message || 'Failed to remove keyword.');
    } finally {
      setSaving(false);
    }
  };

  const getPortfolioSymbols = () => {
    const set = new Set();
    Object.values(portfolioMap || {}).forEach(p => {
      if (p && typeof p === 'object') Object.keys(p).forEach(t => set.add((t || '').toString().trim().toUpperCase()));
    });
    return Array.from(set).filter(Boolean).sort();
  };

  const getWatchlistSymbols = () => {
    const set = new Set();
    Object.values(watchlistMap || {}).forEach(w => {
      if (Array.isArray(w)) w.forEach(t => set.add((t || '').toString().trim().toUpperCase()));
    });
    return Array.from(set).filter(Boolean).sort();
  };

  const toggleEditSymbol = (type, symbol) => {
    setEditingSymbols(prev => ({
      ...prev,
      [type]: { ...prev[type], [symbol]: !prev[type][symbol] }
    }));
  };

  const updatePriceAlertValue = (type, symbol, index, value) => {
    const num = value === '' ? null : Number(value);
    setPriceAlert(prev => {
      const group = prev[type] || {};
      const arr = group[symbol] ? [...group[symbol]] : [null, null];
      arr[index] = num;
      return { ...prev, [type]: { ...group, [symbol]: arr } };
    });
  };

  const handleSavePriceAlert = async (e) => {
    e.preventDefault();
    if (!userEmail) return;
    setSaving(true);
    try {
      const res = await authFetch(`/api/price-alert/${userEmail}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ price_alert: priceAlert }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.message || 'Failed to save');
      setPriceAlert(data.price_alert || priceAlert);
      showMessage('success', 'Price alerts saved.');
    } catch (err) {
      showMessage('error', err.message || 'Failed to save price alerts.');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveRules = async (rules) => {
    if (!userEmail) return;
    setSaving(true);
    try {
      const res = await authFetch(`/api/rules/${userEmail}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ rules }),
      });
      if (!res.ok) throw new Error('Failed to save rules');
      setCustomRules(rules);
      setRulesModalOpen(false);
      loadProfile();
      showMessage('success', 'Custom rules saved.');
    } catch (err) {
      showMessage('error', err.message || 'Failed to save rules.');
    } finally {
      setSaving(false);
    }
  };

  const showPhoneForm = () => {
    setPersonalInfoFormMode('phone');
    setPhoneStep('enter_phone');
    setNewPhoneValue('');
    setPhoneCode('');
    setPhoneError('');
    setPasswordError('');
  };

  const showPasswordForm = () => {
    setPersonalInfoFormMode('password');
    setPhoneError('');
    setPasswordError('');
  };

  const handlePhoneRequest = async (e) => {
    e.preventDefault();
    setPhoneError('');
    const accessToken = getAccessToken();
    if (!accessToken) {
      setPhoneError('Please sign out and sign in again to change your phone number.');
      return;
    }
    const normalized = newPhoneValue.replace(/[\s-]/g, '').replace(/^1/, '+1');
    const withPlus = normalized.startsWith('+') ? normalized : `+${normalized}`;
    if (withPlus.length < 10) {
      setPhoneError('Enter a valid phone number (e.g. +1234567890).');
      return;
    }
    setSaving(true);
    try {
      const res = await fetch('/api/update-phone-request', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ accessToken, newPhoneNumber: withPlus }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.message || 'Failed to send code');
      setPhoneStep('enter_code');
      showMessage('success', 'Verification code sent to your new number.');
    } catch (err) {
      setPhoneError(err.message || 'Failed to send code.');
    } finally {
      setSaving(false);
    }
  };

  const handlePhoneVerify = async (e) => {
    e.preventDefault();
    setPhoneError('');
    const accessToken = getAccessToken();
    if (!accessToken || !userEmail) return;
    if (!phoneCode.trim() || phoneCode.trim().length < 4) {
      setPhoneError('Enter the verification code from your phone.');
      return;
    }
    setSaving(true);
    try {
      const res = await fetch('/api/verify-phone', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: userEmail, accessToken, code: phoneCode.trim() }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.message || 'Verification failed');
      const updatedUser = { ...user, phone: data.phone };
      setUser(updatedUser);
      localStorage.setItem('user', JSON.stringify(updatedUser));
      setPhoneStep('enter_phone');
      setNewPhoneValue('');
      setPhoneCode('');
      showMessage('success', 'Phone number updated.');
    } catch (err) {
      setPhoneError(err.message || 'Verification failed.');
    } finally {
      setSaving(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setPasswordError('');
    if (newPassword.length < 8) {
      setPasswordError('New password must be at least 8 characters.');
      return;
    }
    if (newPassword !== confirmPassword) {
      setPasswordError('New password and confirmation do not match.');
      return;
    }
    const accessToken = getAccessToken();
    if (!accessToken) {
      setPasswordError('Please sign out and sign in again to change your password.');
      return;
    }
    setSaving(true);
    try {
      const res = await fetch('/api/change-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          accessToken,
          previousPassword: currentPassword,
          proposedPassword: newPassword,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.message || 'Failed to change password');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      showMessage('success', 'Password changed.');
    } catch (err) {
      setPasswordError(err.message || 'Failed to change password.');
    } finally {
      setSaving(false);
    }
  };

  const handlePersonalInfoSave = (e) => {
    e.preventDefault();
    if (personalInfoFormMode === 'password') {
      handleChangePassword(e);
    } else if (personalInfoFormMode === 'phone') {
      if (phoneStep === 'enter_phone') {
        handlePhoneRequest(e);
      } else {
        handlePhoneVerify(e);
      }
    }
  };

  if (!user) return null;

  return (
    <div className="page-content profile-page pw-page">
      <div className="pw-header">
        <div className="pw-header-left">
          <div className="pw-header-text">
            <h2 className="pw-title">
              <User size={24} className="pw-title-icon pw-title-icon--profile" aria-hidden />
              <span>My Account</span>
            </h2>
            <p className="pw-subtitle">
              Manage profile, price alerts, message keywords, and trading rules
            </p>
          </div>
        </div>
      </div>

      {message && (
        <div className={`profile-toast profile-toast--${message.type}`} role="alert">
          {message.text}
        </div>
      )}

      {loading ? (
        <div className="pw-loading">
          <div className="pw-spinner" aria-hidden />
          <p>Loading profile…</p>
        </div>
      ) : (
        <div className="profile-layout">
          <nav className="profile-sidebar" aria-label="Account sections">
            <div className="profile-nav-list" role="tablist">
              {PROFILE_SECTIONS.map(({ id, label, icon: Icon }) => (
                <button
                  key={id}
                  type="button"
                  role="tab"
                  aria-selected={activeSection === id}
                  className={`profile-nav-item ${activeSection === id ? 'active' : ''}`}
                  onClick={() => setActiveSection(id)}
                >
                  <Icon size={18} aria-hidden />
                  <span>{label}</span>
                </button>
              ))}
            </div>
          </nav>

          <div className="profile-main">
            {activeSection === 'profile' && (
              <section className="profile-card profile-user-card">
                <h2><User size={20} /> Personal Information</h2>
                <div className="profile-user-info">
                  <div className="profile-user-info-row">
                    <span className="profile-user-info-label">Name</span>
                    <span className="profile-user-info-value">{user.first_name} {user.last_name}</span>
                  </div>
                  <div className="profile-user-info-row">
                    <span className="profile-user-info-label">Email</span>
                    <span className="profile-user-info-value">{user.email}</span>
                  </div>
                  <div className="profile-user-info-row">
                    <span className="profile-user-info-label">Country</span>
                    <span className="profile-user-info-value">
                      {user.country === 'usa' ? 'United States'
                        : user.country === 'canada' ? 'Canada'
                        : user.country || '—'}
                    </span>
                  </div>
                  <div className="profile-user-info-row">
                    <span className="profile-user-info-label">Phone</span>
                    <span className="profile-user-info-value">{user.phone || 'Not set'}</span>
                    <button type="button" className="profile-btn profile-btn-secondary profile-change-field-btn" onClick={showPhoneForm}>
                      {user.phone ? 'Change phone' : 'Add phone'}
                    </button>
                  </div>
                  <div className="profile-user-info-row">
                    <span className="profile-user-info-label">Password</span>
                    <span className="profile-user-info-value">
                      <span className="profile-password-mask">••••••••</span>
                      <span className="profile-password-hint">(hashed, not stored in browser)</span>
                    </span>
                    <button type="button" className="profile-btn profile-btn-secondary profile-change-field-btn" onClick={showPasswordForm}>
                      Change password
                    </button>
                  </div>
                </div>

                <div className="profile-personal-form-inline">
                  <form onSubmit={handlePersonalInfoSave}>
                    {personalInfoFormMode === 'password' && (
                      <>
                        <h4 className="profile-personal-form-title">Change password</h4>
                        <p className="profile-card-desc">Enter your current password and choose a new one (min 8 characters).</p>
                        <input
                          type="password"
                          placeholder="Current password"
                          value={currentPassword}
                          onChange={(e) => setCurrentPassword(e.target.value)}
                          className="profile-input"
                          autoComplete="current-password"
                        />
                        <input
                          type="password"
                          placeholder="New password"
                          value={newPassword}
                          onChange={(e) => setNewPassword(e.target.value)}
                          className="profile-input"
                          autoComplete="new-password"
                        />
                        <input
                          type="password"
                          placeholder="Confirm new password"
                          value={confirmPassword}
                          onChange={(e) => setConfirmPassword(e.target.value)}
                          className="profile-input"
                          autoComplete="new-password"
                        />
                        {passwordError && <p className="profile-field-error">{passwordError}</p>}
                      </>
                    )}
                    {personalInfoFormMode === 'phone' && (
                      <>
                        <h4 className="profile-personal-form-title">Change phone number</h4>
                        {phoneStep === 'enter_phone' ? (
                          <>
                            <p className="profile-card-desc">Enter your new phone number. A verification code will be sent to it.</p>
                            <input
                              type="tel"
                              placeholder="+1234567890"
                              value={newPhoneValue}
                              onChange={(e) => setNewPhoneValue(e.target.value)}
                              className="profile-input"
                            />
                          </>
                        ) : (
                          <>
                            <p className="profile-card-desc">Enter the 6-digit code sent to your new phone number.</p>
                            <input
                              type="text"
                              inputMode="numeric"
                              maxLength={6}
                              placeholder="000000"
                              value={phoneCode}
                              onChange={(e) => setPhoneCode(e.target.value.replace(/\D/g, ''))}
                              className="profile-input"
                            />
                            <button type="button" className="profile-btn profile-btn-secondary" onClick={() => setPhoneStep('enter_phone')}>
                              Back
                            </button>
                          </>
                        )}
                        {phoneError && <p className="profile-field-error">{phoneError}</p>}
                      </>
                    )}
                    <button type="submit" className="profile-btn profile-btn-primary" disabled={saving}>
                      {saving ? 'Saving…' : 'Save'}
                    </button>
                  </form>
                </div>
              </section>
            )}

            {activeSection === 'alerts' && (
              <section className="profile-card">
                <h2><AlertCircle size={20} /> Price Alerts</h2>
                <div className="profile-alert-notification-toggle">
                  <span className="profile-toggle-label">Price alert emails:</span>
                  <div className="profile-alert-preference-toggle" role="group" aria-label="Alert preference">
                    <button
                      type="button"
                      className={`profile-btn profile-btn-secondary ${settings.alert_preference ? 'active' : ''}`}
                      onClick={() => setSettings({ ...settings, alert_preference: true })}
                    >
                      Default
                    </button>
                    <button
                      type="button"
                      className={`profile-btn profile-btn-secondary ${!settings.alert_preference ? 'active' : ''}`}
                      onClick={() => setSettings({ ...settings, alert_preference: false })}
                    >
                      Custom
                    </button>
                  </div>
                  <p className="profile-card-desc profile-toggle-desc">
                    Default: get emails for standard price alerts. Custom: only when price % change meets your low/high thresholds below.
                  </p>
                  <button
                    type="button"
                    className="profile-btn profile-btn-primary"
                    onClick={handleSaveSettings}
                    disabled={saving}
                  >
                    {saving ? 'Saving…' : 'Save'}
                  </button>
                </div>
                <p className="profile-card-desc">Set low/high price percent change alerts for symbols in your portfolio and watchlist. Click Edit to change values, then Save.</p>
                <div className="profile-alert-tabs">
                  <button
                    type="button"
                    className={`profile-alert-tab ${alertTab === 'portfolio' ? 'active' : ''}`}
                    onClick={() => setAlertTab('portfolio')}
                  >
                    Portfolio
                  </button>
                  <button
                    type="button"
                    className={`profile-alert-tab ${alertTab === 'watchlist' ? 'active' : ''}`}
                    onClick={() => setAlertTab('watchlist')}
                  >
                    Watchlist
                  </button>
                </div>
                <div className="profile-alert-grid-wrap">
                  <div className="profile-alert-grid">
                    {(alertTab === 'portfolio' ? getPortfolioSymbols() : getWatchlistSymbols()).map(symbol => {
                      const arr = (priceAlert[alertTab] && priceAlert[alertTab][symbol]) || [null, null];
                      const low = arr[0];
                      const high = arr[1];
                      const isEditing = editingSymbols[alertTab] && editingSymbols[alertTab][symbol];
                      return (
                        <div key={symbol} className="profile-alert-cell">
                          <span className="profile-alert-cell-symbol">{symbol}</span>
                          <input
                            type="number"
                            step="any"
                            placeholder="Low"
                            value={low != null ? low : ''}
                            onChange={(e) => updatePriceAlertValue(alertTab, symbol, 0, e.target.value)}
                            disabled={!isEditing}
                            className="profile-alert-input"
                          />
                          <input
                            type="number"
                            step="any"
                            placeholder="High"
                            value={high != null ? high : ''}
                            onChange={(e) => updatePriceAlertValue(alertTab, symbol, 1, e.target.value)}
                            disabled={!isEditing}
                            className="profile-alert-input"
                          />
                          <button
                            type="button"
                            className="profile-btn profile-btn-secondary profile-alert-edit-btn"
                            onClick={() => toggleEditSymbol(alertTab, symbol)}
                          >
                            <Edit3 size={12} /> {isEditing ? 'Done' : 'Edit'}
                          </button>
                        </div>
                      );
                    })}
                  </div>
                  {(alertTab === 'portfolio' ? getPortfolioSymbols() : getWatchlistSymbols()).length === 0 && (
                    <p className="profile-empty">No {alertTab} symbols. Add symbols in Portfolio or Watchlist pages.</p>
                  )}
                </div>
                <form onSubmit={handleSavePriceAlert}>
                  <button type="submit" className="profile-btn profile-btn-primary" disabled={saving}>
                    {saving ? 'Saving…' : 'Save price alerts'}
                  </button>
                </form>
              </section>
            )}

            {activeSection === 'keywords' && (
              <section className="profile-card">
                <h2><Tag size={20} /> Alert Keywords</h2>
                <p className="profile-card-desc">Add keywords to get notified when they appear in news articles. Manage below.</p>
                <div className="keywords-section">
                  <form onSubmit={handleAddKeyword} className="add-keyword-form">
                    <select
                      value={keywordType}
                      onChange={(e) => setKeywordType(e.target.value)}
                      className="keyword-type-select"
                    >
                      <option value="headline">Headline</option>
                      <option value="content">Content</option>
                      <option value="sentiment">Sentiment</option>
                    </select>
                    <input
                      type="text"
                      value={newKeyword}
                      onChange={(e) => setNewKeyword(e.target.value)}
                      placeholder="Enter keyword"
                      className="keyword-input"
                    />
                    <button type="submit" className="profile-btn profile-btn-primary" disabled={saving}>
                      <Plus size={16} /> Add
                    </button>
                  </form>
                  <div className="current-keywords-section">
                    <h3>Current Keywords</h3>
                    <div className="keywords-container keywords-one-row">
                      {(['headline', 'content', 'sentiment']).map((cat) => {
                        const list = keywords?.[cat] || [];
                        const expanded = keywordCategoryExpanded[cat];
                        const hasKeywords = list.length > 0;
                        const label = cat.charAt(0).toUpperCase() + cat.slice(1);
                        return (
                          <div key={cat} className="keyword-category keyword-category-collapsible">
                            <div className="category-name">
                              <h4>{label}</h4>
                              {hasKeywords && (
                                <button
                                  type="button"
                                  className="keyword-row-toggle"
                                  onClick={() => setKeywordCategoryExpanded(prev => ({ ...prev, [cat]: !prev[cat] }))}
                                  aria-expanded={expanded}
                                  title={expanded ? 'Show one row' : 'Show all'}
                                >
                                  {expanded ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                                </button>
                              )}
                            </div>
                            <div className={`keyword-list horizontal ${expanded ? 'keyword-list-expanded' : 'keyword-list-one-row'}`}>
                              {list.length > 0 ? (
                                list.map((keyword, index) => (
                                  <div key={index} className="keyword-tag">
                                    <span>{keyword}</span>
                                    <button onClick={() => handleRemoveKeyword(cat, keyword)} className="remove-keyword-button" title="Remove keyword">
                                      <X size={14} />
                                    </button>
                                  </div>
                                ))
                              ) : (
                                <p className="no-keywords">No {label.toLowerCase()} keywords added yet</p>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
                <Link to="/messages" className="profile-link-inline">Open Messages →</Link>
              </section>
            )}

            {activeSection === 'rules' && (
              <section className="profile-card">
                <h2><Sliders size={20} /> Custom Trading Rules</h2>
                <p className="profile-card-desc">Buy/sell rules applied on the home dashboard.</p>
                <div className="profile-rules-summary">
                  {customRules && (
                    <p>Buy rules: {customRules.buyRules?.length || 0} · Sell rules: {customRules.sellRules?.length || 0}</p>
                  )}
                </div>
                <button
                  type="button"
                  className="profile-btn profile-btn-secondary"
                  onClick={() => setRulesModalOpen(true)}
                >
                  Manage rules
                </button>
              </section>
            )}
          </div>
        </div>
      )}

      <CustomRulesModal
        isOpen={rulesModalOpen}
        onClose={() => setRulesModalOpen(false)}
        onSave={handleSaveRules}
        currentRules={customRules}
      />
    </div>
  );
}

export default Profile;
